import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useAuth } from './AuthContext';
import { themes as pinkThemes } from '../theme/pinkTheme';
import { themes as midnightPlumThemes } from '../theme/midnightPlumTheme';
import { themes as warmAmberThemes } from '../theme/warmAmberTheme';
import { themes as sageGreenThemes } from '../theme/sageGreenTheme';
import { themes as softCreamThemes } from '../theme/softCreamTheme';
import { themes as berryWineThemes } from '../theme/berryWineTheme';
import { themes as dustyLavenderThemes } from '../theme/dustyLavenderTheme';
import { themes as oceanBreezeThemes } from '../theme/oceanBreezeTheme';
import { themes as sunsetFlameThemes } from '../theme/sunsetFlameTheme';
import { themes as neonArcadeThemes } from '../theme/neonArcadeTheme';
import * as Sentry from '@sentry/react';

const ThemeContext = createContext<any>(undefined);

const paletteThemes = {
  pink: pinkThemes,
  midnightPlum: midnightPlumThemes,
  warmAmber: warmAmberThemes,
  sageGreen: sageGreenThemes,
  softCream: softCreamThemes,
  berryWine: berryWineThemes,
  dustyLavender: dustyLavenderThemes,
  oceanBreeze: oceanBreezeThemes,
  sunsetFlame: sunsetFlameThemes,
  neonArcade: neonArcadeThemes,
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const applyThemeToDocument = (themeMode: string, palette: string) => {
  const paletteObj =
    paletteThemes[palette as keyof typeof paletteThemes] ||
    paletteThemes.midnightPlum;
  const themeObj = paletteObj[themeMode as keyof typeof paletteObj] as Record<
    string,
    string
  >;

  document.body.setAttribute('data-theme', themeMode);
  document.body.setAttribute('data-palette', palette);

  const root = document.documentElement;
  Object.keys(themeObj).forEach((key) => {
    if (key !== 'name') {
      const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(
        cssVarName,
        (themeObj as Record<string, string>)[key]
      );
    }
  });
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const convexUser = useQuery(api.users.getCurrentUserFromAuth);
  const updateProfile = useMutation(api.users.updateProfileFromAuth);

  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  const [colorPalette, setColorPalette] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('colorPalette') || 'midnightPlum';
    }
    return 'midnightPlum';
  });

  const [syncedUserId, setSyncedUserId] = useState<string | null>(null);

  // Sync theme/palette from Convex user when user changes
  // This runs when convexUser loads or user switches accounts
  useEffect(() => {
    if (isAuthenticated && convexUser) {
      // Only sync if this is a different user or first load
      const currentUserId = convexUser._id?.toString() || null;

      if (currentUserId !== syncedUserId) {
        setSyncedUserId(currentUserId);

        // Apply user's saved preferences
        if (convexUser.themeMode) {
          setTheme(convexUser.themeMode);
        }
        if (convexUser.colorPalette) {
          setColorPalette(convexUser.colorPalette);
        }
      }
    } else if (!isAuthenticated && syncedUserId !== null) {
      // User logged out - reset to local storage
      setSyncedUserId(null);
    }
    // Only depend on user ID changes, not the whole convexUser object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, convexUser?._id, syncedUserId]);

  useEffect(() => {
    applyThemeToDocument(theme, colorPalette);
    if (!isAuthenticated) {
      localStorage.setItem('theme', theme);
      localStorage.setItem('colorPalette', colorPalette);
    }
  }, [theme, colorPalette, isAuthenticated]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const updateThemeMode = async (newMode: string) => {
    setTheme(newMode);
    if (isAuthenticated) {
      try {
        await updateProfile({ themeMode: newMode });
      } catch (error) {
        console.error('Failed to save theme mode:', error);
        Sentry.captureException(error);
      }
    }
  };

  const updateColorPalette = async (newPalette: string) => {
    setColorPalette(newPalette);
    if (isAuthenticated) {
      try {
        await updateProfile({ colorPalette: newPalette });
      } catch (error) {
        console.error('Failed to save color palette:', error);
        Sentry.captureException(error);
      }
    }
  };

  const value = {
    theme,
    toggleTheme,
    setTheme: updateThemeMode,
    isDark: theme === 'dark',
    colorPalette,
    setColorPalette: updateColorPalette,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
